import { Link } from "react-router-dom";
import { Archive, Copy, Edit, ExternalLink, Eye, FileText, Plus, Send, Sparkles, Trash2, PenTool } from "lucide-react";
import { ErrorState, LoadingState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { useApi } from "../../hooks/useApi";
import { apiSend } from "../../services/api";
import type { Article, Paged } from "../../types";
import { formatDate } from "../../utils/date";

export function ArticlesPage() {
  const { data, loading, error, setData } = useApi<Paged<Article>>("/api/articles", []);

  async function action(id: number, type: "publish" | "archive" | "delete" | "duplicate") {
    if (["archive", "delete"].includes(type) && !confirm("Confirm this editorial action.")) return;
    if (type === "publish") {
      await apiSend(`/api/articles/${id}/status`, {
        method: "POST",
        body: JSON.stringify({ status: "published" })
      });
    } else if (type === "archive") {
      await apiSend(`/api/articles/${id}/status`, {
        method: "POST",
        body: JSON.stringify({ status: "archived" })
      });
    } else if (type === "delete") {
      await apiSend(`/api/articles/${id}`, { method: "DELETE" });
    } else {
      await apiSend(`/api/articles/${id}/duplicate`, { method: "POST", body: "{}" });
    }
    const refreshed = await fetch("/api/articles", { credentials: "include" }).then((res) => res.json());
    setData(refreshed.data);
  }

  if (loading) return <LoadingState label="Loading newsroom manuscripts..." />;
  if (error) return <ErrorState message={error} />;

  const hasArticles = data && data.items && data.items.length > 0;

  return (
    <div className="admin-page-flow">
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <FileText size={13} className="text-[#C99A2E]" />
            <span>EDITORIAL MANUSCRIPTS</span>
          </div>
          <h1 className="admin-title">Articles & Coverage</h1>
          <p className="admin-subtitle">
            Manage drafts, editorial reviews, scheduled dispatches, and published features.
          </p>
        </div>
        <Link className="admin-btn-primary" to="/admin/articles/new">
          <Plus size={16} />
          <span>New Article</span>
        </Link>
      </header>

      {!hasArticles ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <EmptyState
            icon={FileText}
            badge="EDITORIAL REPOSITORY"
            title="No editorial articles found"
            description="Your newsroom queue is currently clear. Start crafting a feature story, opinion editorial, or news dispatch for ZimSmart Magazine."
            actionLabel="Write First Article"
            actionTo="/admin/articles/new"
            actionIcon={PenTool}
            secondaryActionLabel="Explore Media Library"
            secondaryActionTo="/admin/media"
          />
        </div>
      ) : (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Article & Slug</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Published Date</th>
                  <th>Featured</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <div className="admin-table-title-cell">
                        <Link
                          to={`/admin/articles/${article.id}/edit`}
                          className="admin-table-story-title"
                        >
                          {article.title}
                        </Link>
                        <span className="admin-table-story-slug">/{article.slug}</span>
                      </div>
                    </td>
                    <td>
                      <span className="admin-category-tag">
                        {article.category.name}
                      </span>
                    </td>
                    <td>
                      <span className="admin-author-text">{article.author.name}</span>
                    </td>
                    <td>
                      <span className={`status-pill status-${article.status}`}>
                        <span className="status-dot" />
                        <span>{article.status}</span>
                      </span>
                    </td>
                    <td>
                      <span className="text-xs text-slate-500 font-medium">
                        {formatDate(article.published_at)}
                      </span>
                    </td>
                    <td>
                      {article.featured ? (
                        <span className="admin-featured-badge">Cover Feature</span>
                      ) : (
                        <span className="text-xs text-slate-400">Standard</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-row-actions">
                        <Link
                          title="Edit Manuscript"
                          to={`/admin/articles/${article.id}/edit`}
                          className="action-icon-btn"
                        >
                          <Edit size={15} />
                        </Link>
                        <Link
                          title="View Public Story"
                          to={`/article/${article.slug}`}
                          target="_blank"
                          className="action-icon-btn"
                        >
                          <ExternalLink size={15} />
                        </Link>
                        <button
                          type="button"
                          title="Duplicate Article"
                          onClick={() => action(article.id, "duplicate")}
                          className="action-icon-btn"
                        >
                          <Copy size={15} />
                        </button>
                        {article.status !== "published" && (
                          <button
                            type="button"
                            title="Publish Now"
                            onClick={() => action(article.id, "publish")}
                            className="action-icon-btn publish-btn"
                          >
                            <Send size={15} />
                          </button>
                        )}
                        {article.status !== "archived" && (
                          <button
                            type="button"
                            title="Archive"
                            onClick={() => action(article.id, "archive")}
                            className="action-icon-btn"
                          >
                            <Archive size={15} />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Delete Permanently"
                          onClick={() => action(article.id, "delete")}
                          className="action-icon-btn danger-btn"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

